import React from 'react';
import {useHistory} from 'react-router-dom';
import {Box, Typography, Grid, Button} from '@mui/material';
import NationalDashboardShell from '../components/NationalDashboard.comp';
import KpiCard from '../components/KpiCard.comp';
import MetaInfoBar from '../components/MetaInfoBar.comp';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import {useDashboard} from '../../../context/DashboardContext';
import {findCard, cellText, kpiText, kpiNote, addInfoOf, titleCase, anyPreview, movementSide, movementDelta, isSuppressedKpi, isScopeSuppressed, SUPPRESSED_COPY} from '../../DashboardCommon/dashboardCards';
import {priorityRows, PriorityTable, SUPPRESSED_EMPTY} from '../components/PriorityTable.comp';
import {READINESS_BAND_COLORS, CONTEXT_BAND_COLORS, RISK_BAND_COLORS, BAND_COLOR} from '../constants';

const NAVY   = '#052F44';
const ORANGE = '#FF6900';
const GREY   = '#6A7282';
const BORDER = 'var(--brand-border, #E5E7EB)';

/* National Performance Summary — the one-page read of a partner's country. Live data:
     meta bar          S_3.C_2 ORGCard (PARTNER · COUNTRY · ASSESSMENT WINDOW · REPORT DATE · SOURCE)
     Baseline          S_3.C_3 readiness composite (band, Global avg) · S_3.C_4 any cyber risk (band,
                       Global avg) · S_3.C_5 participation (participants / registered) · S_11.C_4's
                       baseline side for Context & Support
     Recommendations   S_14.C_6 / C_7 — the highlighted Priority Actions rows (same renderer as the
                       Priority Actions pages); "View Full List" opens those pages
     Learning Impact   S_14.C_2 programs created "n of N" · C_3 schools assigned "a/b" · C_5 avg impact
                       (the deck's "Priority Curriculum Coverage" tile left with D-10)
     COS Movement      S_11.C_5 cycle / status and the C_2 · C_3 · C_4 deltas — "Pending" until the
                       country reassesses (cards preview_only) */
const CARD_SECTIONS = ['S_3', 'S_11', 'S_14'];
const LP_PATH = '/dq_pro/national-dashboard/priority-actions/learning-program';
const SI_PATH = '/dq_pro/national-dashboard/priority-actions/system-intervention';

const numeric = (v) => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));
const whole = (v) => String(Math.trunc(Number(v)));
const bandColor = (band, kind) => {
    if (!band) return undefined;
    if (kind === 'risk') return RISK_BAND_COLORS[band] || BAND_COLOR[band] || ORANGE;
    if (kind === 'context') return CONTEXT_BAND_COLORS[band] || BAND_COLOR[band] || ORANGE;
    return READINESS_BAND_COLORS[band] || BAND_COLOR[band] || ORANGE;
};
const globalAvgText = (card, pct) => {
    const g = addInfoOf(card, 'Global avg.');
    return numeric(g) ? `Global avg ${whole(g)}${pct ? '%' : ''}` : undefined;
};

const SectionHeader = ({title, subtitle}) => (
    <Box sx={{mb: 3}}>
        <Typography sx={{fontSize: '23px', fontWeight: 800, color: NAVY, lineHeight: 1.25}}>
            {title}
        </Typography>
        {subtitle && (
            <Typography sx={{fontSize: '14px', fontStyle: 'italic', color: '#6B7280', mt: 0.75, lineHeight: 1.6}}>
                {subtitle}
            </Typography>
        )}
    </Box>
);

const EyebrowLabel = ({children}) => (
    <Typography sx={{fontSize: '11px', fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase', color: GREY, mb: 1.5}}>
        {children}
    </Typography>
);

const outlineBtnSx = {
    borderColor: ORANGE, color: ORANGE, textTransform: 'none', fontWeight: 700, fontSize: '13px',
    borderRadius: '50px', px: 2.25, py: 0.75, whiteSpace: 'nowrap',
    '&:hover': {borderColor: '#e55e00', backgroundColor: 'rgba(255, 105, 0, 0.06)'},
};

const KpiGrid = ({cards, topColor, md = 3}) => (
    <Grid container spacing={2.5}>
        {cards.map((c) => (
            <Grid item xs={12} sm={6} md={md} key={c.label}>
                <KpiCard topColor={c.topColor || topColor} {...c}/>
            </Grid>
        ))}
    </Grid>
);

/* "2 of 6" → {value: "2", subValue: "of 6 priorities"}; anything else as given. */
const programsCreated = (card) => {
    const text = kpiText(card);
    const m = /^(\S+)\s+of\s+(\S+)$/.exec(text);
    return m ? {value: m[1], subValue: `of ${m[2]} priorities`} : {value: text, subValue: 'priority recommendations'};
};
/* "2/11" → coverage 18%. */
const schoolsAssigned = (card) => {
    const text = kpiText(card);
    const m = /^(\d+)\s*\/\s*(\d+)$/.exec(text);
    const pct = m && Number(m[2]) > 0 ? Math.trunc((Number(m[1]) / Number(m[2])) * 100) : null;
    return {value: text, subValue: pct === null ? 'coverage' : `${pct}% coverage`};
};
const movementCard = (label, card, preview) => {
    const delta = movementDelta(card);
    const base = movementSide(card, 'baseline'), re = movementSide(card, 'reassess');
    const suppressed = base.suppressed || re.suppressed;        // a side under the n floor: no delta to show
    return {
        label,
        value: preview ? '—' : delta,
        subValue: suppressed ? SUPPRESSED_COPY : (preview || delta === 'Pending' ? 'awaiting reassess' : `${base.value} → ${re.value}`),
    };
};

const PerformanceSummaryPage = () => {
    const history = useHistory();
    const {dashboardData, loading} = useDashboard({ card_sections: CARD_SECTIONS });
    const s3 = (c) => findCard(dashboardData, 'S_3', c);
    const s11 = (c) => findCard(dashboardData, 'S_11', c);
    const s14 = (c) => findCard(dashboardData, 'S_14', c);

    const metaItems = (s3('C_2')?.table_objects || []).map((obj) => ({
        label: titleCase(obj.column_name),
        value: cellText(obj.values?.[0]),
    }));

    const composite = s3('C_3'), risk = s3('C_4'), participation = s3('C_5');
    const contextBase = movementSide(s11('C_4'), 'baseline');
    const participants = addInfoOf(participation, 'Participants'), registered = addInfoOf(participation, 'Registered');
    // A score KPI under the n floor (suppressed) reads "—" with the reason underneath and no band.
    const scoreKpi = (label, card, pct, kind) => (isSuppressedKpi(card)
        ? {label, value: '—', subValue: kpiNote(card)}
        : {label, value: kpiText(card, pct), subValue: globalAvgText(card, pct), tier: card?.kpi_band || undefined, tierColor: bandColor(card?.kpi_band, kind)});
    const BASELINE_CARDS = [
        scoreKpi('COS Composite', composite, false),
        scoreKpi('Cyber Risk Experience', risk, true, 'risk'),
        {label: 'Participation', value: kpiText(participation, true), subValue: numeric(participants) && numeric(registered) ? `${whole(participants)} / ${whole(registered)}` : undefined},
        contextBase.suppressed
            ? {label: 'Context & Support', value: '—', subValue: SUPPRESSED_COPY}
            : {label: 'Context & Support', value: contextBase.value, tier: contextBase.band || undefined, tierColor: bandColor(contextBase.band, 'context')},
    ].map((c) => ({...c, topColor: c.tierColor || ORANGE}));

    const impact = s14('C_5');
    const LEARNING_IMPACT_CARDS = [
        {label: 'Programs Created', ...programsCreated(s14('C_2'))},
        {label: 'Schools Assigned', ...schoolsAssigned(s14('C_3'))},
        {label: 'Avg Impact Rate', value: kpiText(impact, true), subValue: cellText(addInfoOf(impact, 'reads as')) !== '—' ? cellText(addInfoOf(impact, 'reads as')) : 'Learning signal'},
    ];

    const preview = anyPreview(s11('C_2'), s11('C_3'), s11('C_4'), s11('C_5'));
    const cycle = addInfoOf(s11('C_5'), 'Cycle');
    const COS_MOVEMENT_CARDS = [
        preview || !s11('C_5')
            ? {label: 'Reassessment', value: 'Pending', subValue: 'next COS cycle'}
            : {label: 'Reassessment', value: 'Completed', subValue: cycle ? String(cycle) : cellText(s11('C_5')?.kpi_value)},
        movementCard('Composite Move', s11('C_2'), preview),
        movementCard('Risk Reduction', s11('C_3'), preview),
        movementCard('Context Move', s11('C_4'), preview),
    ];

    const lpRows = priorityRows(s14('C_6'), 'Recommended Learning');
    const siRows = priorityRows(s14('C_7'), 'Recommended Actions');

    if (loading) {
        return (
            <NationalDashboardShell>
                <CircularLoader />
            </NationalDashboardShell>
        );
    }

    if (!dashboardData) {
        return (
            <NationalDashboardShell>
                <NoDataAvailable />
            </NationalDashboardShell>
        );
    }

    return (
    <NationalDashboardShell>
        <SectionHeader
            title="National Performance Summary"
        />

        {metaItems.length > 0 && <MetaInfoBar items={metaItems}/>}

        {/* Baseline */}
        <Box sx={{mb: 4}}>
            <EyebrowLabel>Baseline</EyebrowLabel>
            <KpiGrid cards={BASELINE_CARDS} topColor={ORANGE}/>
        </Box>

        {/* Recommendations — sits between Baseline and Learning Impact, per
            the deck's own annotation on this slide. */}
        <Box sx={{mb: 4, border: `1px solid ${BORDER}`, borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', overflow: 'hidden'}}>
            <Box sx={{p: {xs: 2.5, sm: 3.5}}}>
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2.5}}>
                    <Typography sx={{fontSize: '17px', fontWeight: 800, color: NAVY}}>
                        Highlighted Learning Program Recommendations
                    </Typography>
                    <Button variant="outlined" sx={outlineBtnSx} onClick={() => history.push(LP_PATH)}>View Full List</Button>
                </Box>
                <PriorityTable rows={lpRows} recommendationHeader="Recommended Learning" emptyText={isScopeSuppressed(dashboardData) ? SUPPRESSED_EMPTY : 'No highlighted learning program recommendations.'}/>
            </Box>
            <Box sx={{borderTop: `1px solid ${BORDER}`, p: {xs: 2.5, sm: 3.5}}}>
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2.5}}>
                    <Typography sx={{fontSize: '17px', fontWeight: 800, color: NAVY}}>
                        Highlighted System Intervention Recommendations
                    </Typography>
                    <Button variant="outlined" sx={outlineBtnSx} onClick={() => history.push(SI_PATH)}>View Full List</Button>
                </Box>
                <PriorityTable rows={siRows} recommendationHeader="Recommended Actions" emptyText={isScopeSuppressed(dashboardData) ? SUPPRESSED_EMPTY : 'No highlighted system intervention recommendations.'}/>
            </Box>
        </Box>

        {/* Learning Impact */}
        <Box sx={{mb: 4}}>
            <EyebrowLabel>Learning Impact</EyebrowLabel>
            <KpiGrid cards={LEARNING_IMPACT_CARDS} topColor="#1B6B55" md={4}/>
        </Box>

        {/* COS Movement */}
        <Box sx={{mb: 1.5}}>
            <EyebrowLabel>COS Movement</EyebrowLabel>
            <KpiGrid cards={COS_MOVEMENT_CARDS} topColor="#2E7D32"/>
        </Box>
        <Typography sx={{fontSize: '12px', fontStyle: 'italic', color: GREY}}>
            Movement confirms only after reassessment — learning signal above is intervention evidence, not confirmed improvement.
        </Typography>
    </NationalDashboardShell>
    );
};

export default PerformanceSummaryPage;
